import { useEffect, useState } from 'react';
import { imgSrc } from './image';
import { loadSvg, type SvgNode } from './mascotSvg';

/**
 * La cara calcada de la lámina de una mascota (scripts/trace-clarisa-cara.py): una
 * expresión entera por estado de ánimo, en las mismas coordenadas que su cuerpo. Es
 * del dibujo, no de la mascota: se busca por la URL del cuerpo, y un cuerpo que no está
 * acá (uno subido desde el admin) sigue con la cara dibujada con su configuración.
 */
export const CLARISA_BODY = '/images/mascotas/clarisa.svg';

const ARTS: Record<string, string> = {
  [CLARISA_BODY]: '/images/mascotas/clarisa-cara',
};

export const FACE_KINDS = ['idle', 'happy', 'pensando', 'uy'] as const;
export type FaceKind = (typeof FACE_KINDS)[number];

/** Cada pieza es un árbol con un solo <g>, listo para <MascotBody>. */
export type FacePieces = { cejas: SvgNode[]; ojos: SvgNode[]; nariz: SvgNode[]; boca: SvgNode[] };
export type FaceArt = Record<FaceKind, FacePieces>;

const PIECES = ['cejas', 'ojos', 'nariz', 'boca'] as const;

function loadFaceArt(base: string): Promise<FaceArt> {
  return Promise.all(FACE_KINDS.map((kind) => loadSvg(imgSrc(`${base}-${kind}.svg`)))).then((files) => {
    const art = {} as FaceArt;
    files.forEach((svg, i) => {
      // El orden de los cuatro <g> es el del script.
      if (svg.root.length !== PIECES.length) throw new Error(`${base}-${FACE_KINDS[i]}.svg no tiene ${PIECES.length} piezas`);
      art[FACE_KINDS[i]] = Object.fromEntries(PIECES.map((p, j) => [p, [svg.root[j]]])) as FacePieces;
    });
    return art;
  });
}

// null: no hay (o no cargó). Ausente: se está cargando.
const resolved = new Map<string, FaceArt | null>();

/**
 * La cara calcada del cuerpo `body`: `null` si no tiene, `undefined` mientras carga (sin
 * cara un instante, no una cara dibujada que después cambia). Una vez cargada, las
 * demás mascotas que la usan la tienen en el primer dibujo.
 */
export function useFaceArt(body: string): FaceArt | null | undefined {
  const base = ARTS[body];
  const [, bump] = useState(0);
  useEffect(() => {
    if (!base || resolved.has(base)) return;
    let alive = true;
    loadFaceArt(base)
      .then((art) => resolved.set(base, art))
      .catch(() => resolved.set(base, null))
      .finally(() => alive && bump((n) => n + 1));
    return () => { alive = false; };
  }, [base]);
  return base ? resolved.get(base) : null;
}
